import { useMemo } from 'react';
import {
  SignedIn,
  SignedOut,
  SignIn,
  UserButton,
  useAuth,
  useUser,
} from '@clerk/clerk-react';
import { Refine, type AuthProvider } from '@refinedev/core';
import { ThemedLayout, useNotificationProvider } from '@refinedev/antd';
import routerProvider from '@refinedev/react-router';
import { App as AntApp, ConfigProvider, Flex, Typography, theme } from 'antd';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { AdminApiProvider } from './admin-api-context.js';
import { createAdminApi, createDataProvider } from './api.js';
import { AuditPage } from './pages/audit-page.js';
import { ConfigurationPage } from './pages/configuration-page.js';
import { DashboardPage } from './pages/dashboard-page.js';
import { FinancePage } from './pages/finance-page.js';
import { JobsPage } from './pages/jobs-page.js';
import { ProviderHealthPage } from './pages/provider-health-page.js';
import { RollbackPage } from './pages/rollback-page.js';
import { ValidationPage } from './pages/validation-page.js';

export function App({ apiUrl }: { apiUrl: string }) {
  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#2457d6',
          borderRadius: 10,
          colorBgLayout: '#f4f6fa',
          fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
      }}
    >
      <AntApp>
        <SignedIn>
          <AuthenticatedAdmin apiUrl={apiUrl} />
        </SignedIn>
        <SignedOut>
          <main className="sign-in-shell">
            <div className="sign-in-copy">
              <Typography.Title>PipetinAI</Typography.Title>
              <Typography.Paragraph type="secondary">
                Berktin AI operations and provider-cost administration
              </Typography.Paragraph>
            </div>
            <SignIn routing="hash" />
          </main>
        </SignedOut>
      </AntApp>
    </ConfigProvider>
  );
}

function AuthenticatedAdmin({ apiUrl }: { apiUrl: string }) {
  const { getToken, isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const api = useMemo(
    () => createAdminApi(apiUrl, getToken),
    [apiUrl, getToken],
  );
  const dataProvider = useMemo(
    () => createDataProvider(api, apiUrl),
    [api, apiUrl],
  );
  const authProvider = useMemo<AuthProvider>(
    () => ({
      login: () =>
        Promise.resolve({
          success: false,
          error: new Error('Use the dedicated Clerk sign-in screen'),
        }),
      logout: async () => {
        await signOut();
        return { success: true, redirectTo: '/' };
      },
      check: () =>
        Promise.resolve({
          authenticated: Boolean(isSignedIn),
          ...(!isSignedIn ? { redirectTo: '/' } : {}),
        }),
      onError: (error: unknown) =>
        Promise.resolve({
          ...(isUnauthorized(error) ? { logout: true } : {}),
          error:
            error instanceof Error ? error : new Error('Admin request failed'),
        }),
      getIdentity: () =>
        Promise.resolve({
          id: user?.id ?? 'unknown',
          name:
            user?.fullName ??
            user?.primaryEmailAddress?.emailAddress ??
            'Admin',
          ...(user?.imageUrl ? { avatar: user.imageUrl } : {}),
        }),
    }),
    [isSignedIn, signOut, user],
  );

  return (
    <AdminApiProvider api={api}>
      <BrowserRouter>
        <RefineShell authProvider={authProvider} dataProvider={dataProvider} />
      </BrowserRouter>
    </AdminApiProvider>
  );
}

function RefineShell({
  authProvider,
  dataProvider,
}: {
  authProvider: AuthProvider;
  dataProvider: ReturnType<typeof createDataProvider>;
}) {
  const notificationProvider = useNotificationProvider();
  return (
    <Refine
      authProvider={authProvider}
      dataProvider={dataProvider}
      notificationProvider={notificationProvider}
      routerProvider={routerProvider}
      resources={[
        { name: 'overview', list: '/', meta: { label: 'Overview' } },
        {
          name: 'configuration',
          list: '/configuration',
          meta: { label: 'Configuration' },
        },
        {
          name: 'provider-health',
          list: '/provider-health',
          meta: { label: 'Provider health' },
        },
        { name: 'jobs', list: '/jobs', meta: { label: 'Jobs' } },
        { name: 'rollback', list: '/rollback', meta: { label: 'Rollback' } },
        {
          name: 'validations',
          list: '/validations',
          meta: { label: 'Validation' },
        },
        { name: 'finance', list: '/finance', meta: { label: 'AI finance' } },
        { name: 'audit-events', list: '/audit', meta: { label: 'Audit' } },
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        projectId: 'pipetin-ai',
        title: { text: 'PipetinAI' },
      }}
    >
      <Routes>
        <Route
          element={
            <ThemedLayout Header={AdminHeader} Title={BrandTitle}>
              <Outlet />
            </ThemedLayout>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="configuration" element={<ConfigurationPage />} />
          <Route path="provider-health" element={<ProviderHealthPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="rollback" element={<RollbackPage />} />
          <Route path="validations" element={<ValidationPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Refine>
  );
}

function AdminHeader() {
  return (
    <Flex className="admin-header" justify="flex-end" align="center">
      <UserButton />
    </Flex>
  );
}

function BrandTitle({ collapsed }: { collapsed: boolean }) {
  return (
    <Typography.Text strong className="brand-title">
      {collapsed ? 'P' : 'PipetinAI'}
    </Typography.Text>
  );
}

function isUnauthorized(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    'statusCode' in error &&
    (error as { statusCode?: unknown }).statusCode === 401
  );
}
